import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import { CloseRounded, AutoAwesomeRounded, ArrowBackRounded, CheckRounded, DownloadRounded, EditRounded, AddRounded, DeleteOutlineRounded, HistoryRounded } from '@mui/icons-material';
import {
  getLessonTopics,
  getLessonPlanHistory,
  generateLessonActivities,
  generateLessonPlan,
  saveLessonPlan,
  pushLessonPlan,
  LessonTopic,
  LessonTopicGroup,
  LessonActivity,
  LessonPlanBody,
  LessonPlanHistoryItem,
} from '../../services/api';

/**
 * "Mind Jog" session planner — a guided flow inside Ask the coach.
 * topic → coach suggests 7 activities (multi-select) → coach builds a
 * learn-by-doing plan → edit → push to the class (student coach runs it).
 *
 * Opening the planner always starts a FRESH session (curriculum picker). Every
 * plan the coach builds is saved server-side the moment it's generated, so the
 * "Previous plans" link lists everything generated for this class, view-only.
 */

type Step = 'curriculum' | 'topic' | 'activities' | 'plan' | 'history' | 'history-view';
type Source = 'ncert' | 'global';

interface Props {
  token: string;
  grade: number;
  section: string | null;
  onClose: () => void;
}

const emptyPlan: LessonPlanBody = {
  intro: '', materials: [], duration_min: 0, steps: [], how_it_works: '', result_use: '',
};

// Friendly "26 Aug, 11:58 AM"-style label for a saved plan's timestamp.
const whenLabel = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const LessonPlanner: React.FC<Props> = ({ token, grade, section, onClose }) => {
  const [step, setStep] = useState<Step>('curriculum');
  const [source, setSource] = useState<Source | null>(null);
  const [groups, setGroups] = useState<LessonTopicGroup[]>([]);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [topic, setTopic] = useState<LessonTopic | null>(null);
  const [activities, setActivities] = useState<LessonActivity[]>([]);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [plan, setPlan] = useState<LessonPlanBody>(emptyPlan);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [pushed, setPushed] = useState(false);
  // Which activity is being edited inline (coach-generated or teacher-added).
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null);
  // Previously-generated plans for this class — loaded on demand when the
  // facilitator opens "Previous plans", and one is shown read-only in `viewing`.
  const [history, setHistory] = useState<LessonPlanHistoryItem[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [viewing, setViewing] = useState<LessonPlanHistoryItem | null>(null);

  useEffect(() => {
    getLessonTopics(token, grade).then(setGroups);
  }, [token, grade]);

  // Only selected activities that actually have a title flow into the plan —
  // a blank "add your own" row is ignored until the teacher names it.
  const pickedActivities = useMemo(
    () => activities.filter((a) => a.id && picked[a.id] && a.title.trim()),
    [activities, picked]
  );

  const updateActivity = (id: string, patch: Partial<LessonActivity>) =>
    setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const addCustomActivity = () => {
    const id = `custom-${Date.now()}`;
    setActivities((prev) => [...prev, { id, title: '', summary: '' }]);
    setPicked((p) => ({ ...p, [id]: true }));
    setEditingActivityId(id);
  };

  const removeActivity = (id: string) => {
    setActivities((prev) => prev.filter((a) => a.id !== id));
    setPicked((p) => {
      const next = { ...p };
      delete next[id];
      return next;
    });
    if (editingActivityId === id) setEditingActivityId(null);
  };

  const ncertGroups = useMemo(() => groups.filter((g) => g.source === 'ncert'), [groups]);
  const stemGroups = useMemo(() => groups.filter((g) => g.source === 'global'), [groups]);
  const visibleGroups = useMemo(() => groups.filter((g) => g.source === source), [groups, source]);

  const chooseCurriculum = (s: Source) => {
    setSource(s);
    setOpenGroup(null);
    setStep('topic');
  };

  const chooseTopic = async (t: LessonTopic) => {
    setTopic(t);
    setStep('activities');
    setBusy(true);
    setErr(null);
    try {
      const acts = await generateLessonActivities(token, { grade, section, topic: t });
      setActivities(acts);
      setPicked({});
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to suggest activities.');
    } finally {
      setBusy(false);
    }
  };

  const buildPlan = async () => {
    if (!topic || pickedActivities.length === 0) return;
    setStep('plan');
    setBusy(true);
    setErr(null);
    setPushed(false);
    try {
      const res = await generateLessonPlan(token, { grade, section, topic, activities: pickedActivities });
      setPlan(res.plan || emptyPlan);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to build the plan.');
    } finally {
      setBusy(false);
    }
  };

  const doSave = async (push: boolean) => {
    if (!topic) return;
    setBusy(true);
    setErr(null);
    try {
      const body = { grade, section, topic, activities: pickedActivities, plan };
      if (push) {
        await pushLessonPlan(token, body);
        setPushed(true);
      } else {
        await saveLessonPlan(token, body);
        setErr(null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  // Load the plans generated earlier for this class and show the list.
  const openHistory = async () => {
    setStep('history');
    setViewing(null);
    setHistoryBusy(true);
    setErr(null);
    try {
      const items = await getLessonPlanHistory(token, grade, section);
      setHistory(items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load previous plans.');
    } finally {
      setHistoryBusy(false);
    }
  };

  // Build the VU-format session plan as a downloadable PDF (client-side, jsPDF).
  // Defaults to the plan currently on screen, but the read-only history view
  // passes the selected past plan so its "Download PDF" works too.
  const downloadPdf = (t: LessonTopic | null = topic, pl: LessonPlanBody = plan) => {
    const VIOLET: [number, number, number] = [124, 58, 237];
    const INK: [number, number, number] = [15, 23, 42];
    const MUTED: [number, number, number] = [100, 116, 139];
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 40;
    const contentW = pageW - M * 2;
    let y = M + 6;

    const ensure = (need: number) => {
      if (y + need > pageH - M) {
        doc.addPage();
        y = M + 6;
      }
    };
    const heading = (label: string) => {
      ensure(28);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...VIOLET);
      doc.text(label.toUpperCase(), M, y);
      y += 14;
    };
    const body = (text: string, indent = 0) => {
      if (!text) return;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(...INK);
      const lines = doc.splitTextToSize(text, contentW - indent) as string[];
      lines.forEach((ln) => {
        ensure(15);
        doc.text(ln, M + indent, y);
        y += 15;
      });
      y += 6;
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...VIOLET);
    doc.text('CURIOSITY COACH · SESSION PLAN', M, y);
    y += 20;
    doc.setFontSize(20);
    doc.setTextColor(...INK);
    const titleLines = doc.splitTextToSize(t?.title || 'Session plan', contentW) as string[];
    titleLines.forEach((ln) => { doc.text(ln, M, y); y += 24; });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    const clsLine = `Grade ${grade}${section ? ` · ${section}` : ''}`;
    const dur = pl.duration_min ? ` · ${pl.duration_min} min` : '';
    doc.text(`${clsLine}${dur} · Learn by doing`, M, y);
    y += 24;

    if (pl.intro) { heading('Introduction'); body(pl.intro); }
    if (pl.materials.length) { heading('Materials'); body(pl.materials.map((m) => `•  ${m}`).join('\n')); }
    if (pl.steps.length) {
      heading('Steps');
      pl.steps.forEach((s, i) => {
        ensure(18);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...INK);
        doc.text(s.title || `Step ${i + 1}`, M, y);
        y += 15;
        body(s.detail, 10);
      });
    }
    if (pl.how_it_works) { heading('How it works'); body(pl.how_it_works); }
    if (pl.result_use) { heading("Result & where it's used"); body(pl.result_use); }

    const safe = (t?.title || 'session-plan').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
    doc.save(`lesson-plan-${safe || 'session'}.pdf`);
  };

  const card = 'rounded-2xl border border-[#E9E6F4] bg-white';
  const btnPrimary =
    'inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50';
  const btnGhost =
    'inline-flex items-center gap-1.5 rounded-xl border border-[#E9E6F4] bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-violet-400 hover:text-violet-700';

  // "Previous plans" belongs on the building steps; the history screens have
  // their own back navigation.
  const showHistoryLink = step !== 'history' && step !== 'history-view';

  return (
    <div className={`${card} p-4`}>
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 text-white">
            <AutoAwesomeRounded sx={{ fontSize: 17 }} />
          </span>
          <div>
            <p className="text-sm font-bold text-slate-900">Plan a session</p>
            <p className="text-[0.78rem] text-slate-500">
              {step === 'curriculum' && 'Which curriculum is this session from?'}
              {step === 'topic' && `Pick a ${source === 'ncert' ? 'NCERT' : 'STEM'} topic to plan around.`}
              {step === 'activities' && (topic ? `Activities for “${topic.title}” — pick, edit, or add your own.` : '')}
              {step === 'plan' && (topic ? `Hands-on plan for “${topic.title}”.` : '')}
              {step === 'history' && 'Plans you generated earlier for this class.'}
              {step === 'history-view' && (viewing ? `Saved plan for “${viewing.topic?.title || 'this topic'}” — view only.` : '')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {showHistoryLink && (
            <button type="button" onClick={openHistory}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[0.8rem] font-semibold text-violet-700 transition-colors hover:bg-violet-50">
              <HistoryRounded sx={{ fontSize: 16 }} /> Previous plans
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close planner"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <CloseRounded sx={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      {err && <p className="mb-3 text-[0.82rem] font-medium text-rose-600">{err}</p>}

      {/* STEP 0: curriculum */}
      {step === 'curriculum' && (
        <div className="flex flex-col gap-3">
          {groups.length === 0 && <p className="text-sm text-slate-500">Loading curriculum…</p>}
          {groups.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => chooseCurriculum('ncert')} disabled={ncertGroups.length === 0}
                className="rounded-xl border border-[#E9E6F4] bg-white p-4 text-left transition-colors hover:border-violet-500 disabled:cursor-not-allowed disabled:opacity-50">
                <span className="block text-[0.95rem] font-semibold text-slate-900">NCERT</span>
                <span className="mt-0.5 block text-[0.8rem] text-slate-500">
                  {ncertGroups.length > 0 ? `Standard syllabus · ${ncertGroups.length} subjects` : 'No NCERT chapters for this grade'}
                </span>
              </button>
              <button type="button" onClick={() => chooseCurriculum('global')} disabled={stemGroups.length === 0}
                className="rounded-xl border border-[#E9E6F4] bg-white p-4 text-left transition-colors hover:border-violet-500 disabled:cursor-not-allowed disabled:opacity-50">
                <span className="block text-[0.95rem] font-semibold text-slate-900">STEM</span>
                <span className="mt-0.5 block text-[0.8rem] text-slate-500">
                  {stemGroups.length > 0 ? `Hands-on modules · ${stemGroups.length} modules` : 'No STEM modules available'}
                </span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* STEP 1: topic */}
      {step === 'topic' && (
        <div className="flex flex-col gap-3">
          <button type="button" onClick={() => setStep('curriculum')} className={`${btnGhost} self-start`}>
            <ArrowBackRounded sx={{ fontSize: 16 }} /> Change curriculum
          </button>
          {visibleGroups.length === 0 && <p className="text-sm text-slate-500">No topics in this curriculum.</p>}
          {visibleGroups.map((g) => {
            const open = openGroup === g.label;
            return (
              <div key={g.label} className="rounded-xl border border-[#EFECF9]">
                <button type="button" onClick={() => setOpenGroup(open ? null : g.label)}
                  className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-sm font-semibold text-slate-800">
                  <span>{g.label} <span className="ml-1 text-xs font-normal text-slate-400">{g.topics.length}</span></span>
                  <span className="text-slate-400">{open ? '−' : '+'}</span>
                </button>
                {open && (
                  <div className="flex flex-wrap gap-2 px-3.5 pb-3">
                    {g.topics.map((t) => (
                      <button key={t.title} type="button" onClick={() => chooseTopic(t)}
                        className="rounded-lg border border-[#E9E6F4] bg-white px-3 py-2 text-[0.82rem] font-medium text-slate-700 transition-colors hover:border-violet-500 hover:text-violet-700">
                        {t.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* STEP 2: activities (multi-select) */}
      {step === 'activities' && (
        <div className="flex flex-col gap-3">
          <button type="button" onClick={() => setStep('topic')} className={`${btnGhost} self-start`}>
            <ArrowBackRounded sx={{ fontSize: 16 }} /> Change topic
          </button>
          {busy && <Loading text="The coach is thinking of activities…" />}
          {!busy && activities.map((a) => {
            const on = a.id ? !!picked[a.id] : false;
            const isEditing = !!a.id && a.id === editingActivityId;
            const isCustom = !!a.id && a.id.startsWith('custom-');
            if (isEditing) {
              return (
                <div key={a.id} className="rounded-xl border border-violet-500 bg-violet-50 px-3.5 py-3">
                  <div className="flex flex-col gap-2">
                    <input autoFocus value={a.title} placeholder="Activity title"
                      onChange={(e) => a.id && updateActivity(a.id, { title: e.target.value })}
                      className="w-full rounded-lg border border-[#E9E6F4] bg-white px-2.5 py-1.5 text-[0.88rem] font-semibold text-slate-900 outline-none focus:border-violet-400" />
                    <textarea value={a.summary || ''} placeholder="What do the students actually do?" rows={2}
                      onChange={(e) => a.id && updateActivity(a.id, { summary: e.target.value })}
                      className="w-full rounded-lg border border-[#E9E6F4] bg-white px-2.5 py-1.5 text-[0.85rem] text-slate-700 outline-none focus:border-violet-400" />
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setEditingActivityId(null)}
                        disabled={!a.title.trim()} className={`${btnGhost} !px-3 !py-1.5 disabled:opacity-50`}>
                        Done
                      </button>
                      {isCustom && (
                        <button type="button" onClick={() => a.id && removeActivity(a.id)}
                          className="inline-flex items-center gap-1 rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50">
                          <DeleteOutlineRounded sx={{ fontSize: 16 }} /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={a.id}
                className={`flex items-start gap-2 rounded-xl border px-3.5 py-3 transition-colors ${
                  on ? 'border-violet-500 bg-violet-50' : 'border-[#E9E6F4] bg-white hover:border-violet-300'
                }`}>
                <button type="button" onClick={() => a.id && setPicked((p) => ({ ...p, [a.id!]: !p[a.id!] }))}
                  className="flex flex-1 items-start gap-2.5 text-left">
                  <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md border ${
                    on ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-300 bg-white'
                  }`}>
                    {on && <CheckRounded sx={{ fontSize: 15 }} />}
                  </span>
                  <span>
                    <span className="block text-[0.9rem] font-semibold text-slate-900">{a.title || 'Untitled activity'}</span>
                    {a.summary && <span className="block text-[0.82rem] text-slate-500">{a.summary}</span>}
                  </span>
                </button>
                <button type="button" onClick={() => a.id && setEditingActivityId(a.id)} aria-label="Edit activity"
                  className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-violet-700">
                  <EditRounded sx={{ fontSize: 16 }} />
                </button>
              </div>
            );
          })}
          {!busy && activities.length > 0 && (
            <>
              <button type="button" onClick={addCustomActivity} className={`${btnGhost} self-start`}>
                <AddRounded sx={{ fontSize: 16 }} /> Add your own activity
              </button>
              <button type="button" onClick={buildPlan} disabled={pickedActivities.length === 0} className={btnPrimary}>
                Build plan ({pickedActivities.length} selected)
              </button>
            </>
          )}
        </div>
      )}

      {/* STEP 3: plan */}
      {step === 'plan' && (
        <div className="flex flex-col gap-3">
          <button type="button" onClick={() => setStep('activities')} className={`${btnGhost} self-start`}>
            <ArrowBackRounded sx={{ fontSize: 16 }} /> Back to activities
          </button>
          {busy && !plan.intro && <Loading text="Building your hands-on plan…" />}
          {!busy && (plan.intro || plan.steps.length > 0) && (
            <div className="flex flex-col gap-3 text-[0.9rem] text-slate-800">
              <Field label="Introduction" editing={editing} value={plan.intro} textarea
                onChange={(v) => setPlan((p) => ({ ...p, intro: v }))} />
              <Field label="Materials (one per line)" editing={editing}
                value={plan.materials.join('\n')} textarea
                onChange={(v) => setPlan((p) => ({ ...p, materials: v.split('\n').map((s) => s.trim()).filter(Boolean) }))}
                render={() => <p className="text-slate-600">{plan.materials.join(' · ') || '—'}</p>} />
              <p className="text-[0.78rem] font-semibold uppercase tracking-wide text-slate-400">
                Steps{plan.duration_min ? ` · ${plan.duration_min} min` : ''}
              </p>
              {plan.steps.map((s, i) => (
                <div key={i} className="rounded-xl border border-[#EFECF9] p-3">
                  <p className="text-[0.9rem] font-semibold text-slate-900">{s.title || `Step ${i + 1}`}</p>
                  <Field label="" editing={editing} value={s.detail} textarea
                    onChange={(v) => setPlan((p) => {
                      const st = [...p.steps]; st[i] = { ...st[i], detail: v }; return { ...p, steps: st };
                    })}
                    render={() => <p className="mt-1 whitespace-pre-line text-slate-600">{s.detail}</p>} />
                </div>
              ))}
              <Field label="How it works" editing={editing} value={plan.how_it_works} textarea
                onChange={(v) => setPlan((p) => ({ ...p, how_it_works: v }))} />
              <Field label="Result & where it's used" editing={editing} value={plan.result_use} textarea
                onChange={(v) => setPlan((p) => ({ ...p, result_use: v }))} />

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button type="button" onClick={() => setEditing((v) => !v)} className={btnGhost}>
                  {editing ? 'Done editing' : 'Edit'}
                </button>
                <button type="button" onClick={() => downloadPdf()} className={btnGhost}>
                  <DownloadRounded sx={{ fontSize: 16 }} /> Download PDF
                </button>
                <button type="button" onClick={() => doSave(false)} disabled={busy} className={btnGhost}>
                  Save draft
                </button>
                <button type="button" onClick={() => doSave(true)} disabled={busy} className={btnPrimary}>
                  Push to class
                </button>
                {pushed && (
                  <span className="inline-flex items-center gap-1 text-[0.82rem] font-semibold text-emerald-600">
                    <CheckRounded sx={{ fontSize: 16 }} /> Pushed — students will get this session.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Previous plans — list of everything generated for this class. */}
      {step === 'history' && (
        <div className="flex flex-col gap-3">
          <button type="button" onClick={() => setStep('curriculum')} className={`${btnGhost} self-start`}>
            <ArrowBackRounded sx={{ fontSize: 16 }} /> Back to planning
          </button>
          {historyBusy && <p className="text-sm text-slate-500">Loading previous plans…</p>}
          {!historyBusy && history.length === 0 && (
            <p className="text-sm text-slate-500">No previous plans for this class yet — build one to see it here.</p>
          )}
          {!historyBusy && history.map((h) => (
            <button key={h.id} type="button" onClick={() => { setViewing(h); setStep('history-view'); }}
              className="rounded-xl border border-[#E9E6F4] bg-white px-3.5 py-3 text-left transition-colors hover:border-violet-500">
              <span className="block text-[0.9rem] font-semibold text-slate-900">{h.topic?.title || 'Session plan'}</span>
              <span className="mt-0.5 block text-[0.8rem] text-slate-500">
                {whenLabel(h.created_at)}
                {h.plan?.steps?.length ? ` · ${h.plan.steps.length} steps` : ''}
                {h.plan?.duration_min ? ` · ${h.plan.duration_min} min` : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Previous plan — read-only view of one past plan. */}
      {step === 'history-view' && viewing && (
        <div className="flex flex-col gap-3">
          <button type="button" onClick={() => setStep('history')} className={`${btnGhost} self-start`}>
            <ArrowBackRounded sx={{ fontSize: 16 }} /> Back to previous plans
          </button>
          <ReadOnlyPlan plan={viewing.plan} />
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="button" onClick={() => downloadPdf(viewing.topic, viewing.plan)} className={btnGhost}>
              <DownloadRounded sx={{ fontSize: 16 }} /> Download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// A clear, centered "working…" state — spinner + message — so the facilitator
// knows the coach is busy (the two LLM steps each take a few seconds).
const Loading: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-10 text-center" role="status" aria-live="polite">
    <span className="h-8 w-8 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" aria-hidden="true" />
    <p className="text-sm font-semibold text-slate-700">{text}</p>
    <p className="text-[0.78rem] text-slate-400">This usually takes a few seconds…</p>
  </div>
);

// Read-only render of a saved plan (used by the "Previous plans" view).
const ReadOnlyPlan: React.FC<{ plan: LessonPlanBody }> = ({ plan }) => {
  const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-[0.78rem] font-semibold uppercase tracking-wide text-slate-400">{children}</p>
  );
  return (
    <div className="flex flex-col gap-3 text-[0.9rem] text-slate-800">
      <div>
        <Label>Introduction</Label>
        <p className="mt-0.5 whitespace-pre-line text-slate-700">{plan.intro || '—'}</p>
      </div>
      <div>
        <Label>Materials</Label>
        <p className="mt-0.5 text-slate-600">{plan.materials.join(' · ') || '—'}</p>
      </div>
      <Label>Steps{plan.duration_min ? ` · ${plan.duration_min} min` : ''}</Label>
      {plan.steps.map((s, i) => (
        <div key={i} className="rounded-xl border border-[#EFECF9] p-3">
          <p className="text-[0.9rem] font-semibold text-slate-900">{s.title || `Step ${i + 1}`}</p>
          <p className="mt-1 whitespace-pre-line text-slate-600">{s.detail}</p>
        </div>
      ))}
      <div>
        <Label>How it works</Label>
        <p className="mt-0.5 whitespace-pre-line text-slate-700">{plan.how_it_works || '—'}</p>
      </div>
      <div>
        <Label>Result &amp; where it's used</Label>
        <p className="mt-0.5 whitespace-pre-line text-slate-700">{plan.result_use || '—'}</p>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  editing: boolean;
  textarea?: boolean;
  onChange: (v: string) => void;
  render?: () => React.ReactNode;
}> = ({ label, value, editing, textarea, onChange, render }) => (
  <div>
    {label && <p className="text-[0.78rem] font-semibold uppercase tracking-wide text-slate-400">{label}</p>}
    {editing ? (
      textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2}
          className="mt-1 w-full rounded-lg border border-[#E9E6F4] px-2.5 py-1.5 text-[0.88rem] text-slate-900 outline-none focus:border-violet-400" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full rounded-lg border border-[#E9E6F4] px-2.5 py-1.5 text-[0.88rem] text-slate-900 outline-none focus:border-violet-400" />
      )
    ) : (
      render ? render() : <p className="mt-0.5 text-slate-700">{value || '—'}</p>
    )}
  </div>
);

export default LessonPlanner;
